import { CheckOutlined } from "@ant-design/icons";
import { Trans, useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import {
    AiErrorLLMRejected,
    AILoadingIndicator,
    AIRetry,
    PresetTaskGroup,
    Svg,
} from "@impero-com/ui";
import { rawTransition } from "@impero-com/ui/styles/theme.css";
import { Typography } from "antd";
import { motion } from "motion/react";

import {
    getPostControlsAiSuggestTasksQueryKey,
    usePostControlsAiSuggestTasks,
} from "api/generated/controls/controls";
import type {
    FieldForSubsequentFieldTypeAndString,
    FieldGroupForSubsequentFieldTypeAndString,
} from "api/generated/model";

const { Title } = Typography;

import * as style from "./SuggestAiTasksDrawerContent.css";
import { getFieldTypeInfo } from "./tasks/fieldBuilderUtils";

interface SuggestAiTasksDrawerContentProps {
    controlId: string;
    controlTitle: string;
    description: string;
    controlProgramId: number | null;
    onAddGroup: (
        fields: FieldForSubsequentFieldTypeAndString[],
        suggestionId: string
    ) => void;
}

export function SuggestAiTasksDrawerContent({
    controlId,
    controlTitle,
    description,
    controlProgramId,
    onAddGroup,
}: SuggestAiTasksDrawerContentProps) {
    const { t } = useTranslation();
    const { isPending, isSuccess, data, isError } =
        usePostControlsAiSuggestTasks(
            {
                controlId,
                title: controlTitle,
                description,
                controlProgramId,
            },
            {
                query: {
                    select: (response) => response.data,
                    // We only refetch when query is invalid / params change to reduce calls to AI service
                    refetchOnWindowFocus: false,
                    refetchOnMount: false,
                    staleTime: Infinity,
                },
            }
        );

    const featureHelpText = (
        <span className={style.greyText}>
            <Trans i18nKey="ControlTasks_AIHelpText" />
        </span>
    );

    if (isSuccess && data === null) {
        return (
            <div className={style.root}>
                {featureHelpText}
                <AiErrorLLMRejected
                    message={t("ControlTasks_AIInsufficientContext")}
                    variant="fullWidth"
                />
            </div>
        );
    }
    if (isPending) {
        return (
            <div className={style.root}>
                {featureHelpText}
                <AILoadingIndicator label={t("ControlTasks_Loading")} />
            </div>
        );
    }

    const handleRetry = () => {
        queryClient.invalidateQueries({
            queryKey: getPostControlsAiSuggestTasksQueryKey({
                controlId,
                title: controlTitle,
                description,
                controlProgramId,
            }),
        });
    };

    if (isError || !data) {
        return (
            <div className={style.root}>
                {featureHelpText}

                <AIRetry
                    message={t("ControlTasks_SuggestError")}
                    retryLabel={t("ControlTasks_SuggestRetry")}
                    onRetry={handleRetry}
                />
            </div>
        );
    }

    return (
        <div className={style.root}>
            <div className={style.infoSection}>
                {featureHelpText}

                <div className={style.controlType}>
                    <CheckOutlined />
                    <span className={style.greyText}>
                        {t("ControlTasks_AITaskControlType")}
                    </span>
                    <span className={style.controlTypeTitle}>{data.title}</span>
                </div>
                <span className={style.greyText}>
                    {t("ControlTasks_AIInfoText")}
                </span>
            </div>
            <motion.div
                className={style.taskGroups}
                initial="hidden"
                animate="visible"
                variants={{
                    hidden: {},
                    visible: {
                        transition: {
                            staggerChildren: rawTransition.time / 1000 / 3,
                        },
                    },
                }}
            >
                {data.fieldGroups.map((fieldGroup, index) => (
                    <SuggestAITasksGroup
                        key={fieldGroup.id}
                        fieldGroup={fieldGroup}
                        onAddGroup={(fields) =>
                            onAddGroup(fields, data.suggestionId)
                        }
                        groupTitleLabel={t("ControlTasks_AITasksGroup", {
                            number: index + 1,
                        })}
                    />
                ))}
            </motion.div>
        </div>
    );
}

interface SuggestAITasksGroupProps {
    fieldGroup: FieldGroupForSubsequentFieldTypeAndString;
    onAddGroup: (fields: FieldForSubsequentFieldTypeAndString[]) => void;
    groupTitleLabel: string;
}

function SuggestAITasksGroup({
    fieldGroup,
    onAddGroup,
    groupTitleLabel,
}: SuggestAITasksGroupProps) {
    const { t } = useTranslation();

    const items = fieldGroup.fields.map((field, index) => ({
        id: field.id,
        title: field.title,
        content: field.instructions || undefined,
        icon: <Svg src={getFieldTypeInfo()(field.fieldType.type).svg} />,
        ...(index === 0 && {
            itemButtonLabel: t("ControlTasks_AddAITask"),
            onItemButtonClick: () => onAddGroup(fieldGroup.fields),
        }),
    }));

    return (
        <motion.div
            variants={{
                hidden: { opacity: 0, y: 15 },
                visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                        duration: rawTransition.time / 1000,
                    },
                },
            }}
        >
            <Title level={5}>{groupTitleLabel}</Title>
            <PresetTaskGroup items={items} />
        </motion.div>
    );
}
